import { createSelector } from 'reselect';

import {
  BEAM_AK,
  Flow,
  RINK_OR_RINK_MEMBERSHIP_RELATED,
} from '../../services/viva/constants';
import { isRinkOrRinkMembershipRelated } from '../../services/viva/utils/is-rink-or-rink-membership-related';

import type { RootState } from '..';
import type { AddOnItem } from '../../components/BuyTickets';
import type { CheckBasketItem, CheckOutItem } from '../../services/viva/types';
import type { CheckoutTotals } from '../types';
import type { ShopCartSelection } from '../types/order';

export const selectCheckout = (state: RootState) => {
  return state.checkout;
};

export const selectPayment = (state: RootState) => state.checkout.payment;

export const selectAccountFields = createSelector(
  selectCheckout,
  (checkout) => checkout.account,
);

export const selectAccountAK = createSelector(
  selectAccountFields,
  (account) => account?.accountAKs,
);

export const selectAccountZipCode = createSelector(
  selectAccountFields,
  (account) => account?.address.zip,
);

export const selectCheckoutCart = createSelector(
  selectCheckout,
  (checkout) => checkout?.cart,
);

export const selectCheckoutCartDateTime = createSelector(
  selectCheckoutCart,
  (cart) => cart?.dateTime,
);

export const selectCheckoutPayment = createSelector(
  selectCheckout,
  (checkout) => checkout.payment,
);

export const selectRefund = createSelector(
  selectCheckout,
  (checkout) => checkout.refund,
);

export const selectCheckoutCartSelections = createSelector(
  selectCheckout,
  (checkout) => {
    return checkout.cart.selections;
  },
);

export const selectCheckoutCartAddonSelections = createSelector(
  selectCheckout,
  (checkout) => checkout?.cart.addOnSelections,
);

export const selectCheckoutSelectionsFlat = createSelector(
  selectCheckoutCartSelections,
  (selections) => {
    return Object.values(selections).flat();
  },
);

const dollarMapFn = <T extends ShopCartSelection | AddOnItem>(
  selections: Array<T | undefined>,
) => {
  return (selections.filter((sel) => !!sel) as T[]).map((selection) => ({
    ...selection,
    price: ((selection.price ?? 0) / 100).toFixed(2),
    ...('priceRange' in selection
      ? {
          priceRange: {
            low: ((selection.priceRange?.low ?? 0) / 100).toFixed(2),
            high: ((selection.priceRange?.high ?? 0) / 100).toFixed(2),
          },
        }
      : {}),
    ...('tax' in selection
      ? { tax: ((selection.tax ?? 0) / 100).toFixed(2) }
      : {}),
  }));
};

export const selectCheckoutSelectionsFlatWithDollarAmounts = createSelector(
  selectCheckoutSelectionsFlat,
  (selections) => dollarMapFn<ShopCartSelection>(selections),
);

export const selectCheckoutCartAddonSelectionsFlat = createSelector(
  selectCheckoutCartAddonSelections,
  (selections) => {
    if (!selections) return [];
    const data = Object.values(selections).reduce((acc, innerObj) => {
      return acc.concat(...Object.values(innerObj).flat());
    }, [] as AddOnItem[]);

    const reduced = data.reduce((acc, curr) => {
      const existingItem = acc.find((item) => item.ak === curr.ak);

      if (existingItem && existingItem.quantity) {
        existingItem.quantity += curr.quantity ?? 0;
      } else {
        acc.push({ ...curr, ak: curr.ak, quantity: curr.quantity });
      }

      return acc;
    }, [] as AddOnItem[]);

    return reduced;
  },
);

export const selectCheckoutCartAddonSelectionsFlatWithDollarAmounts =
  createSelector(selectCheckoutCartAddonSelectionsFlat, (selections) =>
    dollarMapFn<AddOnItem>(selections),
  );

export const selectCheckoutCartSelectionsByDate = createSelector(
  selectCheckoutCartSelections,
  (selections) => {
    if (!selections) return [];
    const _selections = Object.values(selections).flat();

    return Object.fromEntries(_selections.map((i) => [i!.dateTime, i]));
  },
);

export const selectCartTotalSelections = createSelector(
  selectCheckoutCart,
  (cart) =>
    Object.keys(cart?.selections ?? {}).reduce(
      (prev, curr) => prev + (cart?.selections?.[curr as Flow]?.length ?? 0),
      0,
    ),
);

export const selectCheckoutCartItems = createSelector(
  selectCheckoutCart,
  (cart) => {
    if (!cart) return [];

    const cartSelectionsFlat = Object.keys(cart.selections).reduce(
      (acc, _flow) => {
        const flow = _flow as Flow;

        return [
          ...acc,
          ...(cart.selections[flow] ?? []).map((sel) => ({
            AK: sel.productAK,
            QTY: sel.count,
            performances: cart.performances[flow][sel.dateTime ?? 0] ?? [],
            capacityGuid: sel.capacityGuid,
            voucherCode: sel.voucherCode,
          })),
        ];
      },
      [] as CheckOutItem[],
    );

    const addOnsFlat: AddOnItem[] = cart?.addOnSelections
      ? Object.keys(cart.addOnSelections).reduce((acc, flow) => {
          const selectionForFlow = cart.addOnSelections[flow as Flow];
          const selections = Object.values(selectionForFlow)
            .flat()
            .filter((item) => {
              if (flow === Flow.TOR_VIP && item.ak === BEAM_AK) {
                return false;
              }

              return true;
            });
          return acc.concat(...selections);
        }, [] as AddOnItem[])
      : [];

    const combined: CheckBasketItem[] = [
      ...cartSelectionsFlat,
      ...addOnsFlat,
    ].map((i) => {
      return {
        AK: 'AK' in i ? i.AK : i.ak,
        QTY: 'QTY' in i ? i.QTY : i.quantity!,
        performances: i.performances ?? [],
        capacityGuid: i.capacityGuid,
      };
    });

    return combined;
  },
);

export const selectCheckoutItemsByFlow = createSelector(
  selectCheckoutCart,
  (cart) => cart.selections,
);

export const selectCheckoutItemsFlat = createSelector(
  selectCheckoutCart,
  (cart) => Object.values(cart?.selections ?? {}).flat(),
);

export const selectCheckoutItemAKs = createSelector(
  selectCheckoutItemsFlat,
  (checkoutItems) => {
    const akArray: string[] = [];
    checkoutItems.forEach((selection) => {
      if (!selection?.productAK) return;
      akArray.push(selection.productAK);
    });
    return akArray;
  },
);

export const selectCheckoutCartTotals = createSelector(
  selectCheckoutCart,
  (cart) =>
    ({
      total: cart?.total ?? 0,
      totalDiscount: cart?.totalDiscount ?? 0,
      totalTax: cart?.totalTax ?? 0,
      totalWithTax: cart?.totalWithTax ?? 0,
      fee: cart?.fee ?? 0,
      couponCode: cart?.couponCode ?? 0,
    } as CheckoutTotals),
);

const countReducer = (selections: (ShopCartSelection | AddOnItem)[]) => {
  return selections.reduce((prev, curr) => {
    if ('count' in curr) {
      return prev + curr.count;
    } else {
      return prev + (curr.quantity ?? 0);
    }
  }, 0);
};

export const selectCheckoutCartSelectionsCount = createSelector(
  selectCheckoutItemsFlat,
  (selections) => countReducer(selections as ShopCartSelection[]),
);

export const selectCheckoutCartAddOnsSelectionsCount = createSelector(
  selectCheckoutCartAddonSelectionsFlat,
  (addOnSelections) => countReducer(addOnSelections),
);

export const selectCheckoutCartSelectionsGroupedByDateTime = createSelector(
  selectCheckoutCartSelections,
  (selectionsByFlow) => {
    const flows = Object.keys(selectionsByFlow) as Flow[];
    const output: { [key in Flow]?: ShopCartSelection[][] } =
      Object.fromEntries(flows.map((flow) => [flow, [[]]]));

    for (const flow of flows) {
      const dateHash: Record<string, ShopCartSelection[]> = {};

      for (const selection of selectionsByFlow[flow] ?? []) {
        if (!selection.dateTime) continue;
        if (!dateHash[selection.dateTime]) dateHash[selection.dateTime] = [];

        dateHash[selection.dateTime].push(selection);
      }

      output[flow] = Object.values(dateHash);
    }

    return output;
  },
);

export const selectCheckoutCartSelectionsByDateTime = createSelector(
  (state: RootState) => state.checkout.cart.selections,
  (state: RootState, flow: Flow) => flow,
  (state: RootState, flow: Flow, dateTime?: number | null) => dateTime,
  (selections, flow, dateTime) => {
    const forFlow = selections[flow];
    return (
      forFlow?.filter((selection) => selection.dateTime === dateTime) || []
    );
  },
);

export const selectCheckoutCartSelectionsByFlowAndDate = createSelector(
  selectCheckoutCartSelectionsByDate,
  (cartSelectionsByDate) => {
    const _selections: { [key in Flow]?: ShopCartSelection[][] } = {};

    for (const key in cartSelectionsByDate) {
      const selection = cartSelectionsByDate[key as Flow];
      // Only add selections that have a length
      if (selection?.length) {
        _selections[key as Flow] = selection;
      }
    }

    return _selections;
  },
);

export const selectCheckoutSelectionsFlows = createSelector(
  selectCheckoutCartSelections,
  (selectionsByFlow) =>
    Object.keys(selectionsByFlow).filter(
      (flow) => (selectionsByFlow[flow as Flow]?.length ?? 0) > 0,
    ) as Flow[],
);

export const selectCheckoutHasRinkSelections = createSelector(
  selectCheckoutCartSelections,
  (selectionsByFlow) =>
    [
      ...selectionsByFlow[Flow.RINK],
      ...selectionsByFlow[Flow.RINK_VIP],
      ...selectionsByFlow[Flow.RINK_MEMBERSHIP],
    ].length > 0,
);

export const selectCartHasTORSelections = createSelector(
  selectCheckoutCartSelections,
  (selectionsByFlow) => {
    const rinkFlows = RINK_OR_RINK_MEMBERSHIP_RELATED;
    let hasTOR = false;

    for (const flow of Object.keys(selectionsByFlow)) {
      if (
        !rinkFlows.includes(flow as Flow) &&
        (selectionsByFlow[flow as Flow]?.length ?? 0) > 0
      ) {
        hasTOR = true;
        break;
      }
    }

    return hasTOR;
  },
);

export const selectCheckoutFlow = createSelector(
  selectCartHasTORSelections,
  (hasTORSelections) => {
    if (hasTORSelections) {
      return Flow.TOR_GA;
    }

    return Flow.RINK;
  },
);

export const selectCheckoutFlowForCheckBasket = createSelector(
  selectCheckoutHasRinkSelections,
  (hasRinkSelections) => {
    if (hasRinkSelections) {
      return Flow.RINK;
    }

    return Flow.TOR_GA;
  },
);

// /**
//  * For all payment related processes, we must provide a merchant ID.
//  * Considering the following scenarios, we need to tell Shift4Fetch which
//  * merchant ID to use:
//  * 1. A cart with only TOR related selections === Flow.TOR_GA
//  * 2. A cart with only Rink related selections === Flow.RINK
//  * 3. A cart with both TOR and Rink related selections === Flow.TOR_GA
//  *
//  * We don't really care to be specific about exactly wihch Flow the cart is
//  * using, we just need to know if it's Rink related or not.
//  *
//  * @returns {Flow.TOR_GA | Flow.RINK} - The Flow to use for payment related processes
//  **/
export const selectCheckoutFlowForPayment = createSelector(
  selectCheckout,
  (checkout) => {
    for (const flow in checkout.cart.selections) {
      if (!isRinkOrRinkMembershipRelated(flow as Flow)) {
        // short circuit if not rink related
        // If there are any selections not related to Rink,
        // we will use the TOR_GA Shift4 MID
        const totalQty =
          checkout.cart.selections[flow as Flow]?.reduce(
            (acc, curr) => acc + curr.count,
            0,
          ) ?? 0;

        if (totalQty > 0) return Flow.TOR_GA;
      }
    }

    // Up to this point, we know that all selections are Rink related
    return Flow.RINK;
  },
);

export const selectSaleAK = createSelector(
  selectCheckoutPayment,
  (payment) => payment?.saleAK,
);

export const selectSunsetPerfAKs = createSelector(
  selectCheckoutPayment,
  (payment) => payment?.sunsetPerfAKs,
);

export const selectInvoice = createSelector(
  selectCheckoutPayment,
  (payment) => payment?.invoice,
);

export const selectCheckoutCoupon = createSelector(
  selectCheckoutCart,
  (cart) => cart?.couponCode,
);
